import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useTrekker } from 'utils/analytics';
import { useCurrencyContext } from '~/context/CurrencyContext';
import { useCurrenciesListContext } from '~/context/CurrencyListContext';

const CSS = require('./CurrencySelect.module.scss');

interface CurrencySelectProps {
  className?: string;
  trackingId: string;
}

function CurrencySelect({ className, trackingId }: CurrencySelectProps) {
  const { state, actions } = useCurrencyContext();
  const { state: currenciesListState } = useCurrenciesListContext();
  const { currencies, loading } = currenciesListState;
  const trekker = useTrekker();
  const queryClient = useQueryClient();

  const options = useMemo(() => {
    return currencies.map(({ symbol_native, code }) => ({
      value: code,
      label: `${code} (${symbol_native})`,
    }));
  }, [currencies]);

  const handleClick = () => {
    trekker.event('click', {
      component: ['CurrencySwitch'],
      target: trackingId,
      action: 'open currency switch',
    });
  };

  const onCurrencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = event.target.value;
    if (newCurrency !== state.currency) {
      actions.setCurrency(newCurrency);
      queryClient.invalidateQueries(['trip']);
      queryClient.invalidateQueries(['stripe-payment-intent']);
      trekker.event('click', {
        component: ['CurrencySwitch'],
        target: trackingId,
        action: `change ${state.currency} to ${newCurrency}`,
      });
    }
  };

  if (loading || options.length === 0) {
    return null;
  }

  return (
    <select
      className={`${CSS.CurrencySelect} ${className || ''}`}
      value={state.currency}
      onClick={handleClick}
      onChange={onCurrencyChange}
      aria-label="Select currency"
    >
      {options.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

export default CurrencySelect;
